import { Maximize2, Sparkles, X } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { MaxwellChat } from "./MaxwellChat";
import { useMaxwellSurface } from "./useMaxwellSurface";

/**
 * Globally-mounted assistant peek rail. The evolution of the old fixed-width
 * `MaxwellPanel`: a desktop-only right rail whose left edge is a drag handle
 * (width persisted via `useMaxwellSurface`), with a pop-out control to the
 * `/maxwell` workspace page and a close control. The conversation body is the
 * shared `<MaxwellChat surface="rail" />`, so popping out to the page never
 * loses context. Visibility rides the surface store's `open` flag (driven by
 * the user-menu "Maxwell" toggle and the rail's own close button).
 */
export function MaxwellRail() {
  const navigate = useNavigate();
  const { open, width, setOpen, setWidth, MIN_W, MAX_W } = useMaxwellSurface();
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const dragStart = useRef<{ x: number; w: number } | null>(null);

  if (!open) return null;

  const renderWidth = dragWidth ?? width;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStart.current = { x: e.clientX, w: width };
    setDragWidth(width);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    // The rail is anchored right; dragging its left edge leftward widens it.
    const next = dragStart.current.w + (dragStart.current.x - e.clientX);
    setDragWidth(Math.min(MAX_W, Math.max(MIN_W, Math.round(next))));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (dragWidth != null) setWidth(dragWidth);
    dragStart.current = null;
    setDragWidth(null);
  };

  return (
    <aside
      style={{ width: renderWidth }}
      className="sticky top-chrome hidden h-[calc(100vh-3.5rem-var(--viewing-as-bar-height))] shrink-0 self-start border-l border-border bg-card lg:flex relative"
    >
      {/* Left-edge drag handle. A thin hit-area straddling the border; the
          inner bar lights up on hover/drag so the affordance reads. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Maxwell rail"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="group/handle absolute left-0 top-0 z-10 flex h-full w-2 -translate-x-1/2 cursor-col-resize items-stretch justify-center"
      >
        <span
          className={cn(
            "w-px bg-transparent transition-colors group-hover/handle:bg-primary",
            dragWidth != null && "bg-primary",
          )}
        />
      </div>

      <div className="flex min-h-0 w-full flex-col">
        <header className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-accent-dim text-brand-accent">
            <Sparkles size={16} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-sm font-semibold text-foreground">Maxwell</span>
            <span className="truncate text-xs text-muted-foreground">Your sales assistant</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => navigate("/maxwell/chat")}
            aria-label="Open Maxwell workspace"
            title="Open workspace"
          >
            <Maximize2 size={15} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setOpen(false)}
            aria-label="Close Maxwell rail"
            title="Close"
          >
            <X size={15} />
          </Button>
        </header>

        <MaxwellChat surface="rail" />
      </div>
    </aside>
  );
}
